import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FiEdit2, FiEye, FiPlus, FiSearch, FiTrash2, FiX } from 'react-icons/fi';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import SimonOrderModal from '../components/SimonOrderModal';

const SimonOrderPage = () => {
    const [orders, setOrders] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [products, setProducts] = useState([]);
    const [bundles, setBundles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalOrders, setTotalOrders] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [supplierFilter, setSupplierFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [supplierSummary, setSupplierSummary] = useState({
        Doraemon: { totalComms: 0, totalReceivable: 0, pendingPayable: 0, totalBonus: 0, totalBomb: 0 },
        Bean: { totalComms: 0, totalReceivable: 0, pendingPayable: 0, totalBonus: 0, totalBomb: 0 }
    });
    const [weeklyBySupplier, setWeeklyBySupplier] = useState({ Doraemon: [], Bean: [] });

    const formatMoney = (value) => `$${Number(value || 0).toFixed(2)}`;

    const formatWeekRange = (startIso, endIso) => {
        const opts = { day: '2-digit', month: 'short', year: '2-digit' };
        return `${new Date(startIso).toLocaleDateString('en-SG', opts)} - ${new Date(endIso).toLocaleDateString('en-SG', opts)}`;
    };

    const formatWeeklyLine = (w) => {
        const range = formatWeekRange(w.weekStart, w.weekEnd);
        const { deliveredComms, creditComms, debitComms, totalComms, deliveredCount } = w;
        const hasCredit = Math.abs(Number(creditComms || 0)) >= 0.005;
        const hasDebit = Math.abs(Number(debitComms || 0)) >= 0.005;
        let mid;
        if (!hasCredit && !hasDebit) {
            mid = `${formatMoney(totalComms)} (${deliveredCount} orders)`;
        } else {
            const parts = [];
            if (hasCredit) parts.push(`\u{1F381} ${formatMoney(creditComms)}`);
            if (hasDebit) parts.push(`\u{1F4A3} ${formatMoney(debitComms)}`);
            mid = `${formatMoney(deliveredComms)} + ${parts.join(' + ')} = ${formatMoney(totalComms)} (${deliveredCount} orders)`;
        }
        return `${range} : ${mid}`;
    };

    const fetchMeta = async () => {
        try {
            const token = localStorage.getItem('token');
            const headers = {
                Authorization: `Bearer ${token}`,
                'ngrok-skip-browser-warning': 'true'
            };
            const [customerResponse, productResponse, bundleResponse] = await Promise.all([
                axios.get(`${process.env.REACT_APP_API_URL}/api/simoncustomers?page=1&limit=500`, { headers }),
                axios.get(`${process.env.REACT_APP_API_URL}/api/simonproducts?page=1&limit=500`, { headers }),
                axios.get(`${process.env.REACT_APP_API_URL}/api/simonbundles?page=1&limit=500`, { headers })
            ]);

            if (customerResponse.data.success) setCustomers(customerResponse.data.data || []);
            if (productResponse.data.success) setProducts(productResponse.data.data || []);
            if (bundleResponse.data.success) setBundles(bundleResponse.data.data || []);
        } catch (err) {
            // Non-blocking
        }
    };

    const fetchSupplierSummary = async () => {
        try {
            const token = localStorage.getItem('token');
            const headers = {
                Authorization: `Bearer ${token}`,
                'ngrok-skip-browser-warning': 'true'
            };

            const [orderStatsResponse, receivableStatsResponse] = await Promise.all([
                axios.get(`${process.env.REACT_APP_API_URL}/api/simonorders/stats`, { headers }),
                axios.get(`${process.env.REACT_APP_API_URL}/api/simonreceivables/stats`, { headers })
            ]);

            const commsBySupplier = orderStatsResponse.data?.data?.totalCommsBySupplier || {};
            const partnerComms = orderStatsResponse.data?.data?.supplierPartnerComms || {};
            const receivableBySupplierRows = receivableStatsResponse.data?.data?.totalBySupplier || [];

            const receivableBySupplier = { Doraemon: 0, Bean: 0 };
            for (const row of receivableBySupplierRows) {
                if (row?._id === 'Doraemon' || row?._id === 'Bean') {
                    receivableBySupplier[row._id] = Number(row.totalSGD ?? row.totalAmount ?? 0);
                }
            }

            const dP = partnerComms.Doraemon || {};
            const bP = partnerComms.Bean || {};
            const next = {
                Doraemon: {
                    totalComms: Number(commsBySupplier.Doraemon || 0),
                    totalReceivable: Number(receivableBySupplier.Doraemon || 0),
                    pendingPayable: Number(commsBySupplier.Doraemon || 0) - Number(receivableBySupplier.Doraemon || 0),
                    totalBonus: Number(dP.totalBonus ?? 0),
                    totalBomb: Number(dP.totalBomb ?? 0)
                },
                Bean: {
                    totalComms: Number(commsBySupplier.Bean || 0),
                    totalReceivable: Number(receivableBySupplier.Bean || 0),
                    pendingPayable: Number(commsBySupplier.Bean || 0) - Number(receivableBySupplier.Bean || 0),
                    totalBonus: Number(bP.totalBonus ?? 0),
                    totalBomb: Number(bP.totalBomb ?? 0)
                }
            };

            setSupplierSummary(next);
        } catch (err) {
            // Non-blocking summary cards
        }
    };

    const fetchWeeklySummary = async () => {
        try {
            const token = localStorage.getItem('token');
            const headers = {
                Authorization: `Bearer ${token}`,
                'ngrok-skip-browser-warning': 'true'
            };
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/simonorders/weekly-summary`, { headers });
            if (res.data?.success && res.data?.data) {
                setWeeklyBySupplier({
                    Doraemon: res.data.data.Doraemon || [],
                    Bean: res.data.data.Bean || []
                });
            }
        } catch {
            // non-blocking
        }
    };

    const fetchOrders = async (page = 1, search = '', supplier = 'all', orderStatus = 'all') => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            let apiUrl = `${process.env.REACT_APP_API_URL}/api/simonorders?page=${page}&limit=20`;
            if (search) apiUrl += `&search=${encodeURIComponent(search)}`;
            if (supplier !== 'all') apiUrl += `&supplier=${encodeURIComponent(supplier)}`;
            if (orderStatus !== 'all') apiUrl += `&orderStatus=${encodeURIComponent(orderStatus)}`;

            const response = await axios.get(apiUrl, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'ngrok-skip-browser-warning': 'true'
                }
            });

            if (response.data.success) {
                setOrders(response.data.data);
                setTotalOrders(response.data.pagination.total);
                setTotalPages(response.data.pagination.pages);
            }
        } catch (err) {
            setError(`Failed to fetch Simon orders: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMeta();
        fetchSupplierSummary();
        fetchWeeklySummary();
    }, []);

    useEffect(() => {
        fetchOrders(currentPage, searchTerm, supplierFilter, statusFilter);
    }, [currentPage, searchTerm, supplierFilter, statusFilter]);

    const handleSearch = () => {
        setSearchTerm(searchInput);
        setCurrentPage(1);
    };

    const handleClear = () => {
        setSearchInput('');
        setSearchTerm('');
        setSupplierFilter('all');
        setStatusFilter('all');
        setCurrentPage(1);
    };

    const handleDelete = async () => {
        if (!selectedOrder) return;
        try {
            setDeleteLoading(true);
            const token = localStorage.getItem('token');
            await axios.delete(`${process.env.REACT_APP_API_URL}/api/simonorders/${selectedOrder._id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setShowDeleteModal(false);
            setSelectedOrder(null);
            fetchOrders(currentPage, searchTerm, supplierFilter, statusFilter);
            fetchSupplierSummary();
            fetchWeeklySummary();
        } catch (err) {
            setError(`Failed to delete Simon order: ${err.response?.data?.error || err.message}`);
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <Layout>
            <div className="px-2 w-full max-w-none p-6">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 min-w-0">
                        <h3 className="text-lg font-semibold text-blue-900 mb-2">
                            Doraemon ({formatMoney(supplierSummary.Doraemon.pendingPayable)})
                        </h3>
                        <div className="text-sm space-y-1">
                            <div><span className="font-semibold text-gray-700">Total Comms:</span> <span className="text-blue-700">{formatMoney(supplierSummary.Doraemon.totalComms)}</span></div>
                            <div><span className="font-semibold text-gray-700">Total Receivable (SGD):</span> <span className="text-blue-700">{formatMoney(supplierSummary.Doraemon.totalReceivable)}</span></div>
                            <div><span className="font-semibold text-gray-700">Pending Payable:</span> <span className="text-green-700 font-semibold">{formatMoney(supplierSummary.Doraemon.pendingPayable)}</span></div>
                            <div><span className="font-semibold text-gray-700">Credit:</span> <span className="text-emerald-700">{formatMoney(supplierSummary.Doraemon.totalBonus)}</span> <span className="text-gray-500 text-xs">(SUPDORAEMON · credit orders)</span></div>
                            <div><span className="font-semibold text-gray-700">Debit:</span> <span className="text-red-700">{formatMoney(supplierSummary.Doraemon.totalBomb)}</span> <span className="text-gray-500 text-xs">(SUPDORAEMON · debit orders)</span></div>
                            <div><span className="font-semibold text-gray-700">Total Bonus:</span>{' '}
                                <span className={Number(supplierSummary.Doraemon.totalBonus || 0) + Number(supplierSummary.Doraemon.totalBomb || 0) >= 0 ? 'text-emerald-700 font-semibold' : 'text-red-700 font-semibold'}>
                                    {formatMoney(Number(supplierSummary.Doraemon.totalBonus || 0) + Number(supplierSummary.Doraemon.totalBomb || 0))}
                                </span>{' '}
                                <span className="text-gray-500 text-xs">(Credit + Debit)</span>
                            </div>
                        </div>
                    </div>
                    <div className="bg-green-50 border border-green-100 rounded-lg p-4 min-w-0">
                        <h3 className="text-lg font-semibold text-green-900 mb-2">
                            Bean ({formatMoney(supplierSummary.Bean.pendingPayable)})
                        </h3>
                        <div className="text-sm space-y-1">
                            <div><span className="font-semibold text-gray-700">Total Comms:</span> <span className="text-blue-700">{formatMoney(supplierSummary.Bean.totalComms)}</span></div>
                            <div><span className="font-semibold text-gray-700">Total Receivable (SGD):</span> <span className="text-blue-700">{formatMoney(supplierSummary.Bean.totalReceivable)}</span></div>
                            <div><span className="font-semibold text-gray-700">Pending Payable:</span> <span className="text-green-700 font-semibold">{formatMoney(supplierSummary.Bean.pendingPayable)}</span></div>
                            <div><span className="font-semibold text-gray-700">Credit:</span> <span className="text-emerald-700">{formatMoney(supplierSummary.Bean.totalBonus)}</span> <span className="text-gray-500 text-xs">(SUPBEAN · credit orders)</span></div>
                            <div><span className="font-semibold text-gray-700">Debit:</span> <span className="text-red-700">{formatMoney(supplierSummary.Bean.totalBomb)}</span> <span className="text-gray-500 text-xs">(SUPBEAN · debit orders)</span></div>
                            <div><span className="font-semibold text-gray-700">Total Bonus:</span>{' '}
                                <span className={Number(supplierSummary.Bean.totalBonus || 0) + Number(supplierSummary.Bean.totalBomb || 0) >= 0 ? 'text-emerald-700 font-semibold' : 'text-red-700 font-semibold'}>
                                    {formatMoney(Number(supplierSummary.Bean.totalBonus || 0) + Number(supplierSummary.Bean.totalBomb || 0))}
                                </span>{' '}
                                <span className="text-gray-500 text-xs">(Credit + Debit)</span>
                            </div>
                        </div>
                    </div>
                    <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 min-w-0">
                        <h3 className="text-lg font-semibold text-indigo-900 mb-2">Doraemon Past Weekly Record</h3>
                        <p className="text-xs text-gray-600 mb-2">Monday–Sunday (Singapore). Dollar = sum of order <span className="font-semibold">totalComms</span>; order count = <span className="font-semibold">delivered</span> only.</p>
                        <div className="text-sm space-y-1.5 font-mono leading-snug">
                            {(weeklyBySupplier.Doraemon || []).map((w) => (
                                <div key={w.weekStart} className="text-gray-800">{formatWeeklyLine(w)}</div>
                            ))}
                        </div>
                    </div>
                    <div className="bg-amber-50 border border-amber-100 rounded-lg p-4 min-w-0">
                        <h3 className="text-lg font-semibold text-amber-900 mb-2">Bean Past Weekly Record</h3>
                        <p className="text-xs text-gray-600 mb-2">Monday–Sunday (Singapore). Dollar = sum of order <span className="font-semibold">totalComms</span>; order count = <span className="font-semibold">delivered</span> only.</p>
                        <div className="text-sm space-y-1.5 font-mono leading-snug">
                            {(weeklyBySupplier.Bean || []).map((w) => (
                                <div key={w.weekStart} className="text-gray-800">{formatWeeklyLine(w)}</div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold">Simon Orders</h1>
                        <p className="text-gray-600">Total: {totalOrders} orders</p>
                    </div>
                    <button onClick={() => setShowCreateModal(true)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center">
                        <FiPlus className="mr-2" />
                        Create New Order
                    </button>
                </div>

                <div className="mb-6">
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <input
                                type="text"
                                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-blue-700 font-bold"
                                placeholder="Search by customer ID, name, phone, supplier, or source..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                            />
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                <FiSearch className="h-5 w-5 text-gray-400" />
                            </div>
                        </div>
                        <select value={supplierFilter} onChange={(e) => { setSupplierFilter(e.target.value); setCurrentPage(1); }} className="px-4 py-2 border border-gray-300 rounded-md">
                            <option value="all">All Suppliers</option>
                            <option value="Bean">Bean</option>
                            <option value="Doraemon">Doraemon</option>
                        </select>
                        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }} className="px-4 py-2 border border-gray-300 rounded-md">
                            <option value="all">All Statuses</option>
                            <option value="delivered">Delivered</option>
                            <option value="cancelled">Cancelled</option>
                            <option value="credit">Credit</option>
                            <option value="debit">Debit</option>
                        </select>
                        <button onClick={handleSearch} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"><FiSearch className="mr-2" />Search</button>
                        <button onClick={handleClear} className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700">Clear</button>
                    </div>
                </div>

                {error && <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">{error}</div>}

                {showCreateModal && (
                    <SimonOrderModal
                        mode="create"
                        customers={customers}
                        products={products}
                        bundles={bundles}
                        onSuccess={() => {
                            setShowCreateModal(false);
                            fetchOrders(currentPage, searchTerm, supplierFilter, statusFilter);
                            fetchSupplierSummary();
                            fetchWeeklySummary();
                        }}
                        onCancel={() => setShowCreateModal(false)}
                    />
                )}

                {showEditModal && selectedOrder && (
                    <SimonOrderModal
                        key={`simon-order-edit-${selectedOrder._id}`}
                        mode="edit"
                        order={selectedOrder}
                        customers={customers}
                        products={products}
                        bundles={bundles}
                        onSuccess={() => {
                            setShowEditModal(false);
                            setSelectedOrder(null);
                            fetchOrders(currentPage, searchTerm, supplierFilter, statusFilter);
                            fetchSupplierSummary();
                            fetchWeeklySummary();
                        }}
                        onCancel={() => {
                            setShowEditModal(false);
                            setSelectedOrder(null);
                        }}
                    />
                )}

                {showDeleteModal && selectedOrder && (
                    <DeleteConfirmationModal
                        title="Delete Simon Order"
                        message="Are you sure you want to delete this order?"
                        itemName={`${selectedOrder.customerID} - ${selectedOrder.orderDate ? new Date(selectedOrder.orderDate).toLocaleDateString('en-SG') : 'No Date'}`}
                        isLoading={deleteLoading}
                        onConfirm={handleDelete}
                        onCancel={() => {
                            setShowDeleteModal(false);
                            setSelectedOrder(null);
                        }}
                    />
                )}

                {showViewModal && selectedOrder && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                        <div className="relative top-20 mx-auto p-5 border max-w-6xl w-full shadow-lg rounded-md bg-white">
                            <div className="flex justify-between items-center border-b pb-4 mb-4">
                                <h3 className="text-xl font-medium">
                                    Simon Order Details - <span className="text-blue-700 font-bold">{selectedOrder.customerID}</span>
                                </h3>
                                <button onClick={() => { setShowViewModal(false); setSelectedOrder(null); }} className="text-gray-500 hover:text-gray-700">
                                    <FiX className="w-6 h-6" />
                                </button>
                            </div>
                            <div className="space-y-6">
                                <section className="bg-gray-50 p-4 rounded-lg">
                                    <h4 className="text-lg font-medium mb-3">Order Snapshot</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                        <div><span className="font-semibold text-gray-700">Customer:</span> <span className="text-blue-700">{selectedOrder.customerID} - {selectedOrder.name || 'N/A'}</span></div>
                                        <div><span className="font-semibold text-gray-700">Phone:</span> <span className="text-blue-700">{selectedOrder.phoneNumber || 'N/A'}</span></div>
                                        <div><span className="font-semibold text-gray-700">WhatsApp:</span> <span className="text-blue-700">{selectedOrder.whatsappNumber || 'N/A'}</span></div>
                                        <div><span className="font-semibold text-gray-700">Address:</span> <span className="text-blue-700">{selectedOrder.address || 'N/A'}</span></div>
                                        <div><span className="font-semibold text-gray-700">Telegram:</span> <span className="text-blue-700">{[selectedOrder.telegram?.tgName, selectedOrder.telegram?.tgNumber, selectedOrder.telegram?.tgID].filter(Boolean).join(' - ') || 'N/A'}</span></div>
                                        <div><span className="font-semibold text-gray-700">Remarks:</span> <span className="text-blue-700 whitespace-pre-wrap">{selectedOrder.remarks || 'N/A'}</span></div>
                                        <div><span className="font-semibold text-gray-700">Order Date:</span> <span className="text-blue-700">{selectedOrder.orderDate ? new Date(selectedOrder.orderDate).toLocaleDateString('en-SG') : 'N/A'}</span></div>
                                        <div><span className="font-semibold text-gray-700">Delivery Date:</span> <span className="text-blue-700">{selectedOrder.deliveryDate ? new Date(selectedOrder.deliveryDate).toLocaleDateString('en-SG') : 'N/A'}</span></div>
                                        <div><span className="font-semibold text-gray-700">Status:</span> <span className="text-blue-700">{selectedOrder.orderStatus}</span></div>
                                        <div><span className="font-semibold text-gray-700">Supplier:</span> <span className="text-blue-700">{[selectedOrder.supplier, selectedOrder.supplierSource].filter(Boolean).join(' - ')}</span></div>
                                    </div>
                                </section>

                                <section className="bg-gray-50 p-4 rounded-lg">
                                    <h4 className="text-lg font-medium mb-3">Products</h4>
                                    {(selectedOrder.products || []).length === 0 ? (
                                        <div className="text-sm text-gray-500">No products</div>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                                <thead className="bg-gray-100">
                                                    <tr>
                                                        <th className="px-3 py-2 text-left">Category</th>
                                                        <th className="px-3 py-2 text-left">Product</th>
                                                        <th className="px-3 py-2 text-right">Qty</th>
                                                        <th className="px-3 py-2 text-right">Price</th>
                                                        <th className="px-3 py-2 text-right">Cost</th>
                                                        <th className="px-3 py-2 text-left">Remarks</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {(selectedOrder.products || []).map((item, index) => (
                                                        <tr key={`${selectedOrder._id}-product-${index}`}>
                                                            <td className="px-3 py-2">{item.product?.category || 'N/A'}</td>
                                                            <td className="px-3 py-2">{item.product?.productName || 'N/A'}</td>
                                                            <td className="px-3 py-2 text-right">{item.quantity || 0}</td>
                                                            <td className="px-3 py-2 text-right">${Number(item.price || 0).toFixed(2)}</td>
                                                            <td className="px-3 py-2 text-right">${Number(item.cost || 0).toFixed(2)}</td>
                                                            <td className="px-3 py-2 whitespace-pre-wrap break-words">{item.remarks || '—'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </section>

                                <section className="bg-gray-50 p-4 rounded-lg">
                                    <h4 className="text-lg font-medium mb-3">Bundles</h4>
                                    {(selectedOrder.bundles || []).length === 0 ? (
                                        <div className="text-sm text-gray-500">No bundles</div>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                                <thead className="bg-gray-100">
                                                    <tr>
                                                        <th className="px-3 py-2 text-left">Bundle</th>
                                                        <th className="px-3 py-2 text-right">Qty</th>
                                                        <th className="px-3 py-2 text-right">Price</th>
                                                        <th className="px-3 py-2 text-right">Cost</th>
                                                        <th className="px-3 py-2 text-left">Remarks</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {(selectedOrder.bundles || []).map((item, index) => (
                                                        <tr key={`${selectedOrder._id}-bundle-${index}`}>
                                                            <td className="px-3 py-2">{item.bundleProduct?.bundleName || 'N/A'}</td>
                                                            <td className="px-3 py-2 text-right">{item.quantity || 0}</td>
                                                            <td className="px-3 py-2 text-right">${Number(item.price || 0).toFixed(2)}</td>
                                                            <td className="px-3 py-2 text-right">${Number(item.cost || 0).toFixed(2)}</td>
                                                            <td className="px-3 py-2 whitespace-pre-wrap break-words">{item.remarks || '—'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </section>

                                {(selectedOrder.creditDebits || []).length > 0 && (
                                    <section className="bg-gray-50 p-4 rounded-lg">
                                        <h4 className="text-lg font-medium mb-3">Credit / debit (misc)</h4>
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                                <thead className="bg-gray-100">
                                                    <tr>
                                                        <th className="px-3 py-2 text-left">Type</th>
                                                        <th className="px-3 py-2 text-right">Amount</th>
                                                        <th className="px-3 py-2 text-left">Remarks</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {(selectedOrder.creditDebits || []).map((row, idx) => (
                                                        <tr key={`cd-${idx}`}>
                                                            <td className="px-3 py-2 capitalize">{row.type || '—'}</td>
                                                            <td className="px-3 py-2 text-right">${Number(row.amount || 0).toFixed(2)}</td>
                                                            <td className="px-3 py-2 whitespace-pre-wrap break-words">{row.remarks || '—'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-2">
                                            Commission shown below includes these lines (credit adds, debit subtracts).
                                        </p>
                                    </section>
                                )}

                                <section className="bg-gray-50 p-4 rounded-lg">
                                    <h4 className="text-lg font-medium mb-3">Totals</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 text-sm">
                                        <div><span className="font-semibold text-gray-700">Order Delivery Fees:</span> <span className="text-blue-700">${Number(selectedOrder.orderDeliveryFees || 0).toFixed(2)}</span></div>
                                        <div><span className="font-semibold text-gray-700">Supplier Delivery Fees:</span> <span className="text-blue-700">${Number(selectedOrder.supplierDeliveryFees || 0).toFixed(2)}</span></div>
                                        <div><span className="font-semibold text-gray-700">Total Price:</span> <span className="text-blue-700">${Number(selectedOrder.totalPrice || 0).toFixed(2)}</span></div>
                                        <div><span className="font-semibold text-gray-700">Total Cost:</span> <span className="text-blue-700">${Number(selectedOrder.totalCost || 0).toFixed(2)}</span></div>
                                        <div><span className="font-semibold text-gray-700">Total Comms:</span> <span className="text-green-700 font-semibold">${Number(selectedOrder.totalComms || 0).toFixed(2)}</span></div>
                                    </div>
                                </section>
                            </div>
                        </div>
                    </div>
                )}

                <div className="bg-white rounded-lg shadow overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Supplier</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Price</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Cost</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Comms</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr><td colSpan="8" className="px-6 py-4 text-center">Loading...</td></tr>
                            ) : orders.length === 0 ? (
                                <tr><td colSpan="8" className="px-6 py-4 text-center">No Simon orders found</td></tr>
                            ) : (
                                orders.map((order) => (
                                    <tr key={order._id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">{order.orderDate ? new Date(order.orderDate).toLocaleDateString('en-SG') : 'N/A'}</td>
                                        <td className="px-6 py-4 text-sm">{order.customerID} - {order.name || 'N/A'}</td>
                                        <td className="px-6 py-4 text-sm">{[order.supplier, order.supplierSource].filter(Boolean).join(' - ')}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm capitalize">{order.orderStatus}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">${Number(order.totalPrice || 0).toFixed(2)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">${Number(order.totalCost || 0).toFixed(2)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-green-700 font-semibold">${Number(order.totalComms || 0).toFixed(2)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                            <button onClick={() => { setSelectedOrder(order); setShowViewModal(true); }} className="p-1.5 text-blue-600 hover:text-blue-900 hover:bg-blue-100 rounded-full inline-flex items-center" title="View Details"><FiEye className="w-4 h-4" /></button>
                                            <button onClick={() => { setSelectedOrder(order); setShowEditModal(true); }} className="p-1.5 text-green-600 hover:text-green-900 hover:bg-green-100 rounded-full inline-flex items-center" title="Edit Order"><FiEdit2 className="w-4 h-4" /></button>
                                            <button onClick={() => { setSelectedOrder(order); setShowDeleteModal(true); }} className="p-1.5 text-red-600 hover:text-red-900 hover:bg-red-100 rounded-full inline-flex items-center" title="Delete Order"><FiTrash2 className="w-4 h-4" /></button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </div>
        </Layout>
    );
};

export default SimonOrderPage;
